import type {
  EscopoMacroPlanejamento,
  PlanejamentoControle,
  TipoObraPlanejamento,
} from "@/modules/financeiro-obras";
import { formatarStatusFinanceiro } from "@/modules/financeiro-obras";
import { localizarEscopo, localizarTipo } from "./planejamento-controle.cancelamento";
import { formatClassificacao, formatMilhoes, formatMoeda } from "./planejamento-controle.format";
import type { EscopoATransferir } from "./transferir-escopo-macro-dialog";
import type { TipoATransferir } from "./transferir-tipo-obra-dialog";

/** A transferência só abre com o Escopo ativo e sem solicitação pendente (cancelamento ou outra transferência). */
export function transferenciaBloqueada({
  situacao,
  solicitacaoPendente,
  edicaoProjecao,
}: EscopoMacroPlanejamento) {
  return (
    situacao === "CANCELADO" ||
    solicitacaoPendente !== "NENHUMA" ||
    edicaoProjecao.bloqueio === "SALDO_A_DESTINAR"
  );
}

export function montarEscopoATransferir(
  planejamento: PlanejamentoControle,
  obraEscopoMacroId: string,
): EscopoATransferir | null {
  const achado = localizarEscopo(planejamento, obraEscopoMacroId);
  if (!achado) return null;
  const { centro, tipo, escopo } = achado;
  const { obra } = planejamento;

  return {
    obraEscopoMacroId,
    nome: escopo.nome,
    tipoObraCodigo: tipo.tipoObra.codigo,
    tipoObra: `${tipo.tipoObra.codigo} · ${tipo.tipoObra.nome}`,
    centroCusto: `${centro.principal ? "Centro de custo principal" : centro.nome} · ${centro.codigo}`,
    classificacao: formatClassificacao(escopo.classificacao),
    projecao:
      escopo.possuiProjecao && escopo.totais
        ? formatMoeda(escopo.totais.outlook.total, "BRL")
        : null,
    dataInicio: escopo.dataInicio,
    dataFim: escopo.dataFim,
    obraOrigem: {
      codigo: obra.codigo,
      fel: formatarStatusFinanceiro({ codigo: obra.fel }),
      embarcacao: obra.embarcacao?.nome ?? null,
    },
  };
}

export const escoposVigentesDoTipo = ({ escoposMacro }: TipoObraPlanejamento) =>
  escoposMacro.filter((escopo) => escopo.situacao !== "CANCELADO");

export const tipoSemEscopoVigente = (tipo: TipoObraPlanejamento) =>
  escoposVigentesDoTipo(tipo).length === 0;

export function transferenciaTipoBloqueada(tipo: TipoObraPlanejamento) {
  return (
    tipo.situacao !== "APROVADO" ||
    tipo.solicitacaoPendente !== "NENHUMA" ||
    tipo.escoposMacro.some((escopo) => escopo.solicitacaoPendente !== "NENHUMA") ||
    tipoSemEscopoVigente(tipo)
  );
}

export function montarTipoATransferir(
  planejamento: PlanejamentoControle,
  obraTipoObraId: string,
): TipoATransferir | null {
  const achado = localizarTipo(planejamento, obraTipoObraId);
  if (!achado) return null;
  const { centro, tipo } = achado;
  const { obra } = planejamento;
  const totalDoTipo = planejamento.linhas.find(
    (linha) => linha.chave === `tipo:${obraTipoObraId}`,
  )?.totais;

  return {
    obraTipoObraId,
    codigo: tipo.tipoObra.codigo,
    nome: tipo.tipoObra.nome,
    classificacao: formatClassificacao(tipo.classificacao),
    centroCusto: `${centro.principal ? "Centro de custo principal" : centro.nome} · ${centro.codigo}`,
    quantidadeEscopos: escoposVigentesDoTipo(tipo).length,
    valorTipo: totalDoTipo ? formatMoeda(totalDoTipo.outlook.total, "BRL") : null,
    outlookOrigem: formatMilhoes(planejamento.totais.outlook.total),
    obraOrigem: {
      codigo: obra.codigo,
      fel: formatarStatusFinanceiro({ codigo: obra.fel }),
      embarcacao: obra.embarcacao?.nome ?? null,
    },
  };
}
